import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Settings, Save, Check, X, Eye, EyeOff, RefreshCw, Copy, 
  CheckCircle, XCircle, Loader2, ExternalLink, Globe, 
  CreditCard, MessageCircle, MapPin, Car, ToggleLeft, ToggleRight,
  Link as LinkIcon, Webhook
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';
// Always show production domain for webhook URLs (these are configured in external services)
const WEBHOOK_BASE_URL = 'https://goturkeytransfer.com';

const AdminIntegrationsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [formData, setFormData] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);

  // Webhook URLs
  const webhookUrls = [
    {
      provider: 'stripe',
      name: 'Stripe',
      url: `${WEBHOOK_BASE_URL}/api/webhooks/stripe`,
      events: ['payment_intent.succeeded', 'payment_intent.payment_failed', 'checkout.session.completed']
    },
    {
      provider: 'paypal',
      name: 'PayPal',
      url: `${WEBHOOK_BASE_URL}/api/webhooks/paypal`,
      events: ['CHECKOUT.ORDER.APPROVED', 'PAYMENT.CAPTURE.COMPLETED', 'PAYMENT.CAPTURE.DENIED']
    },
    {
      provider: 'whatsapp',
      name: 'WhatsApp Business',
      url: `${WEBHOOK_BASE_URL}/api/webhooks/whatsapp`,
      events: ['messages', 'message_status']
    },
    {
      provider: 'booking',
      name: 'Booking Status',
      url: `${WEBHOOK_BASE_URL}/api/webhooks/booking-status`,
      events: ['status_update']
    }
  ];

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchIntegrations();
  }, [navigate]);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/integrations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setIntegrations(data.integrations);
        
        // Initialize form data
        const initialFormData = {};
        data.integrations.forEach(integration => {
          initialFormData[integration.key] = {
            enabled: integration.enabled,
            environment: integration.environment || (integration.environments?.[0] || null),
            config: { ...integration.config }
          };
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const currentData = formData[key] || {};
    const newEnabled = !currentData.enabled;
    
    setFormData(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: newEnabled }
    }));

    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/integrations/${key}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: newEnabled })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`${key} ${newEnabled ? 'enabled' : 'disabled'}`);
        fetchIntegrations();
      }
    } catch (error) {
      toast.error('Failed to toggle integration');
      setFormData(prev => ({
        ...prev,
        [key]: { ...prev[key], enabled: !newEnabled }
      }));
    }
  };

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const data = formData[key];
      
      const response = await fetch(`${API_BASE}/api/admin/integrations/${key}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Settings saved');
        fetchIntegrations();
      } else {
        toast.error(result.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleTest = async (key) => {
    setTesting(prev => ({ ...prev, [key]: true }));
    setTestResults(prev => ({ ...prev, [key]: null }));
    
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/integrations/${key}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      setTestResults(prev => ({ ...prev, [key]: result }));
      
      if (result.success) {
        toast.success(result.message || 'Connection successful');
      } else {
        toast.error(result.message || 'Connection failed');
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [key]: { success: false, message: 'Test failed' } }));
      toast.error('Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleConfigChange = (key, field, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        config: {
          ...prev[key]?.config,
          [field]: value
        }
      }
    }));
  };

  const handleEnvironmentChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        environment: value
      }
    }));
  };

  const toggleReveal = (key, field) => {
    const secretKey = `${key}_${field}`;
    setRevealedSecrets(prev => ({
      ...prev,
      [secretKey]: !prev[secretKey]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getIcon = (key) => {
    switch (key) {
      case 'zuppin': return <Car className="h-5 w-5" />;
      case 'google_maps': return <MapPin className="h-5 w-5" />;
      case 'paypal': return <CreditCard className="h-5 w-5" />;
      case 'stripe': return <CreditCard className="h-5 w-5" />;
      case 'whatsapp_business': return <MessageCircle className="h-5 w-5" />;
      default: return <Globe className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
        <AdminSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground" data-testid="integrations-title">
              API & Integrations
            </h1>
            <p className="text-muted-foreground">
              Manage external service connections and API credentials
            </p>
          </div>

          {/* Webhook URLs Section */}
          <Card className="bg-secondary border-border mb-8">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhook & Callback URLs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Configure these URLs in your external service dashboards to receive event notifications.
              </p>
              <div className="space-y-4">
                {webhookUrls.map((webhook) => (
                  <div key={webhook.provider} className="p-4 bg-background rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">{webhook.name}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(webhook.url)}
                        data-testid={`copy-webhook-${webhook.provider}`}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <code className="text-xs text-primary bg-muted px-2 py-1 rounded block mb-2 break-all">
                      {webhook.url}
                    </code>
                    <div className="flex flex-wrap gap-1">
                      {webhook.events.map((event) => (
                        <span key={event} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                          {event}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Integration Cards */}
          <div className="space-y-4">
            {integrations.map((integration) => {
              const data = formData[integration.key] || {};
              const testResult = testResults[integration.key];
              
              return (
                <Card 
                  key={integration.key}
                  className={`bg-secondary border-border transition-all ${
                    data.enabled ? 'border-green-500/30' : ''
                  }`}
                >
                  <CardContent className="p-6">
                    {/* Card Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${
                          data.enabled 
                            ? 'bg-green-500/10 text-green-500' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {getIcon(integration.key)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground flex items-center gap-2">
                            {integration.name}
                            {data.enabled && (
                              <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded">
                                Active
                              </span>
                            )}
                            {testResult && (
                              <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                                testResult.success 
                                  ? 'bg-green-500/10 text-green-500' 
                                  : 'bg-red-500/10 text-red-500'
                              }`}>
                                {testResult.success ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                {testResult.success ? 'Connected' : 'Failed'}
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-muted-foreground">{integration.description}</p>
                          {integration.updated_at && (
                            <p className="text-xs text-muted-foreground/60 mt-1">
                              Last updated: {new Date(integration.updated_at).toLocaleString()} by {integration.updated_by}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedCard(expandedCard === integration.key ? null : integration.key)}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          {expandedCard === integration.key ? 'Close' : 'Configure'}
                        </Button>
                        <button
                          onClick={() => handleToggle(integration.key)}
                          className={`p-2 rounded-lg transition-colors ${
                            data.enabled 
                              ? 'text-green-500 hover:bg-green-500/10' 
                              : 'text-muted-foreground hover:bg-muted'
                          }`}
                          data-testid={`toggle-${integration.key}`}
                        >
                          {data.enabled ? (
                            <ToggleRight className="h-8 w-8" />
                          ) : (
                            <ToggleLeft className="h-8 w-8" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Configuration */}
                    {expandedCard === integration.key && (
                      <div className="mt-6 pt-6 border-t border-border">
                        {/* Environment Selector */}
                        {integration.has_environment && (
                          <div className="mb-4">
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Environment
                            </label>
                            <select
                              value={data.environment || ''}
                              onChange={(e) => handleEnvironmentChange(integration.key, e.target.value)}
                              className="w-full max-w-xs px-3 py-2 bg-background border border-border rounded-md text-foreground"
                              data-testid={`env-${integration.key}`}
                            >
                              {integration.environments?.map((env) => (
                                <option key={env} value={env}>
                                  {env.charAt(0).toUpperCase() + env.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Credential Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {integration.fields?.map((field) => {
                            const isSecret = field.type === 'secret';
                            const secretKey = `${integration.key}_${field.key}`;
                            const isRevealed = revealedSecrets[secretKey];
                            const currentValue = data.config?.[field.key] || '';
                            const isSet = currentValue && (currentValue.includes('*') || currentValue === 'set');
                            
                            return (
                              <div key={field.key}>
                                <label className="text-sm font-medium text-foreground block mb-2">
                                  {field.label}
                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <div className="relative">
                                  <Input
                                    type={isSecret && !isRevealed ? 'password' : 'text'}
                                    value={currentValue}
                                    onChange={(e) => handleConfigChange(integration.key, field.key, e.target.value)}
                                    placeholder={isSet ? '••••••••' : `Enter ${field.label}`}
                                    className="bg-background pr-10"
                                    disabled={!data.enabled && false}
                                    data-testid={`input-${integration.key}-${field.key}`}
                                  />
                                  {isSecret && (
                                    <button
                                      type="button"
                                      onClick={() => toggleReveal(integration.key, field.key)}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                      {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                  )}
                                </div>
                                {isSet && (
                                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                                    <Check className="h-3 w-3" /> Value is set (enter new value to change)
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Test Result Message */}
                        {testResult && testResult.message && (
                          <div className={`p-3 rounded-lg mb-4 ${
                            testResult.success 
                              ? 'bg-green-500/10 text-green-500' 
                              : 'bg-red-500/10 text-red-500'
                          }`}>
                            <p className="text-sm">{testResult.message}</p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={() => handleTest(integration.key)}
                            disabled={testing[integration.key]}
                            variant="outline"
                            data-testid={`test-${integration.key}`}
                          >
                            {testing[integration.key] ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Test Connection
                          </Button>
                          <Button
                            onClick={() => handleSave(integration.key)}
                            disabled={saving[integration.key]}
                            data-testid={`save-${integration.key}`}
                          >
                            {saving[integration.key] ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminIntegrationsPage;
